export type VideoPlatform = 'youtube' | 'instagram' | 'tiktok';

export interface ParsedVideoLink {
  platform: VideoPlatform | null;
  videoId: string | null;
  needsResolution: boolean;
}

const PLATFORM_PATTERNS: Array<{ platform: VideoPlatform; regex: RegExp }> = [
  { platform: 'tiktok', regex: /^https?:\/\/(www\.|vm\.|vt\.)?tiktok\.com\/.+/ },
  { platform: 'instagram', regex: /^https?:\/\/(www\.)?instagram\.com\/.+/ },
  { platform: 'youtube', regex: /^https?:\/\/(www\.)?(youtube\.com\/(shorts\/|watch\?|live\/).+|youtu\.be\/.+)/ },
];

export function detectPlatform(url: string): VideoPlatform | null {
  const trimmed = url.trim();
  for (const { platform, regex } of PLATFORM_PATTERNS) {
    if (regex.test(trimmed)) return platform;
  }
  return null;
}

function extractId(url: string, platform: VideoPlatform): string | null {
  try {
    const parsed = new URL(url);
    if (platform === 'instagram') {
      // /reel/CODE, /reels/CODE, /p/CODE, /tv/CODE — share links (/share/...) need resolution
      const match = parsed.pathname.match(/^\/(reel|reels|p|tv)\/([A-Za-z0-9_-]+)/);
      return match ? match[2] : null;
    }
    if (platform === 'tiktok') {
      // /@user/video/ID, /video/ID, /@user/photo/ID — short links (vm./vt./t/) need resolution
      const match = parsed.pathname.match(/\/(?:video|photo)\/(\d+)/);
      return match ? match[1] : null;
    }
    if (platform === 'youtube') {
      if (parsed.hostname === 'youtu.be') return parsed.pathname.slice(1).split('/')[0] || null;
      if (parsed.searchParams.has('v')) return parsed.searchParams.get('v');
      const match = parsed.pathname.match(/\/(shorts|embed|v|live)\/([^/?]+)/);
      return match ? match[2] : null;
    }
  } catch {
    /* malformed URL */
  }
  return null;
}

export function parseVideoLink(url: string): ParsedVideoLink {
  const platform = detectPlatform(url);
  if (!platform) return { platform: null, videoId: null, needsResolution: false };
  const videoId = extractId(url.trim(), platform);
  return { platform, videoId, needsResolution: videoId === null };
}

export function videoKey(url: string): string | null {
  const { platform, videoId } = parseVideoLink(url);
  return platform && videoId ? `${platform}:${videoId}` : null;
}
